import moment from "moment";
import "moment/locale/es";
import { cuentaMask, dineroDisabledMask, round2Decimals } from "../../Components/Items";
import conceptoConTipoDeCambio from "../conceptoTipoCambio";
import { pdfStylesV2, divisorV2, seccionHeadingV2, applyPdfColors, PDF_COLORS, PDF_PAGE_MARGINS, PDF_LINE_HEIGHT, PDF_LABEL_FONT, pdfFz } from "../../Components/PdfComponents/componentsv2.0/stylesPDFv2";
import { PDFHeaderV2 } from "../../Components/PdfComponents/componentsv2.0/PDFHeader";
import { PDFCompanyInfoV2 } from "../../Components/PdfComponents/componentsv2.0/PDFCompanyInfo";
import { PDFItemsTableV2 } from "../../Components/PdfComponents/componentsv2.0/PDFItemsTable";
import { PDFSummarySectionV2 } from "../../Components/PdfComponents/componentsv2.0/PDFTotalsSummary";
import { PDFDataTableV2 } from "../../Components/PdfComponents/componentsv2.0/PDFDataTable";
import { PDFAvisoV2 } from "../../Components/PdfComponents/componentsv2.0/PDFAviso";
import { PDFBrandFooterConNumeroV2 } from "../../Components/PdfComponents/componentsv2.0/PDFFooter";
// Registra la tipografía Mulish en pdfMake (efecto secundario) y expone su nombre.
import MULISH_FONT from "./mulishFont";
// Registra la variante Mulish SemiBold (600) usada en las etiquetas (efecto secundario).
import "./mulishSemiBoldFont";

moment.locale("es");

// Catálogo fijo de tipos de póliza (Poliza.tipodepoliza.tipoDePolizaId).
const TIPOS_DE_POLIZA = {
  1: { nombre: "Ingreso", inicial: "I" },
  2: { nombre: "Egreso", inicial: "E" },
  3: { nombre: "Traslado", inicial: "T" },
  4: { nombre: "Nómina", inicial: "N" },
  5: { nombre: "Pago", inicial: "P" },
  6: { nombre: "Diario", inicial: "D" },
};

const SIN_PROYECTO = "SIN PROYECTO";

const polizaPdfV2 = (values) => {
  const empresa = values.empresa;
  const poliza = values.poliza;
  const logoBiwo = values.logoBiwo?.base64Completa;

  applyPdfColors(empresa);

  const detallada = !!values.detallada;
  const documentos = values.documentos || [];
  const movimientos = values.cargosabonos || [];
  const mostrarRegimen = !empresa?.deshabilitarRegimenFiscal;

  const { nombre: ied = "", inicial: iedInicial = "" } = TIPOS_DE_POLIZA[poliza?.tipodepoliza?.tipoDePolizaId] || {};

  let cargosTotal = 0;
  let abonosTotal = 0;
  const proyectosDistintos = new Set(movimientos.map((valueCA) => valueCA.proyectoId || valueCA.proyecto?._id));
  const mostrarColumnaProyecto = proyectosDistintos.size > 1;
  const cuentasPorCrear = movimientos.filter((valueCA) => valueCA.cuentaPorConfirmar);
  const nombreProyecto = (valueCA) => valueCA.proyecto?.nombre || SIN_PROYECTO;
  const claveProyecto = (valueCA) => (valueCA.proyecto?.nombre ? `${valueCA.proyectoId || valueCA.proyecto._id}` : "");
  const codigoCuenta = (valueCA) => {
    const cuenta = valueCA.cuentacontable;
    if (!cuenta) return "";
    return `${cuenta.nivel1 || ""}${cuenta.nivel2 || ""}${cuenta.nivel3 || ""}${cuenta.nivel4 || ""}${cuenta.nivel5 || ""}`;
  };

  const cargosabonos = mostrarColumnaProyecto
    ? [...movimientos].sort((a, b) => {
      const sinProyectoA = !a.proyecto?.nombre;
      const sinProyectoB = !b.proyecto?.nombre;
      if (sinProyectoA !== sinProyectoB) return sinProyectoA ? 1 : -1;
      return nombreProyecto(a).localeCompare(nombreProyecto(b), "es", { sensitivity: "base", numeric: true })
        || (b.cargo ? 1 : 0) - (a.cargo ? 1 : 0)
        || codigoCuenta(a).localeCompare(codigoCuenta(b), "es", { numeric: true })
        || `${a.concepto || ""}`.localeCompare(`${b.concepto || ""}`, "es", { sensitivity: "base" });
    })
    : movimientos;

  const resumenProyectos = [];
  // Con mostrarColumnaProyecto la tabla siempre trae 6 columnas: el rótulo abarca las
  // primeras cuatro y los importes se alinean con Cargo y Abono.
  const filaSubtotal = (grupo) => ([
    { text: "Subtotal " + grupo.nombre, colSpan: 4, style: "textoTablaChico", bold: true, alignment: "left", fillColor: PDF_COLORS.tint },
    {}, {}, {},
    { text: dineroDisabledMask(grupo.cargos), style: "textoTablaChico", bold: true, alignment: "right", fillColor: PDF_COLORS.tint },
    { text: dineroDisabledMask(grupo.abonos), style: "textoTablaChico", bold: true, alignment: "right", fillColor: PDF_COLORS.tint },
  ]);

  const cargosAbonosTable = [];
  let grupoActual = null;
  for (const valueCA of cargosabonos) {
    let cargos = 0;
    let abonos = 0;
    if (valueCA.cargo) {
      cargos = valueCA.monto;
      cargosTotal += valueCA.monto;
    } else {
      abonos = valueCA.monto;
      abonosTotal += valueCA.monto;
    }
    if (mostrarColumnaProyecto) {
      const clave = claveProyecto(valueCA);
      if (!grupoActual || grupoActual.clave !== clave) {
        if (grupoActual) cargosAbonosTable.push(filaSubtotal(grupoActual));
        grupoActual = { clave, nombre: nombreProyecto(valueCA), movimientos: 0, cargos: 0, abonos: 0 };
        resumenProyectos.push(grupoActual);
      }
      grupoActual.movimientos += 1;
      grupoActual.cargos = round2Decimals(grupoActual.cargos + cargos);
      grupoActual.abonos = round2Decimals(grupoActual.abonos + abonos);
    }
    const marca = valueCA.cuentaPorConfirmar ? " *" : "";
    cargosAbonosTable.push([
      {
        text: cuentaMask(valueCA.cuentacontable.nivel1 + valueCA.cuentacontable.nivel2 + valueCA.cuentacontable.nivel3 + valueCA.cuentacontable.nivel4 + valueCA.cuentacontable.nivel5) + marca,
        style: "textoTablaChico",
        alignment: "left",
        ...(valueCA.cuentaPorConfirmar ? { bold: true } : {}),
      },
      { text: valueCA.cuentacontable.descripcion, style: "textoTablaChico", alignment: "left" },
      ...(mostrarColumnaProyecto ? [{ text: valueCA.proyecto?.nombre || "", style: "textoTablaChico", alignment: "left", color: PDF_COLORS.label }] : []),
      { text: conceptoConTipoDeCambio(valueCA.concepto, valueCA.tipoDeCambio), style: "textoTablaChico", alignment: "left" },
      { text: cargos === 0 ? "-" : dineroDisabledMask(cargos), style: "textoTablaChico", alignment: "right" },
      { text: abonos === 0 ? "-" : dineroDisabledMask(abonos), style: "textoTablaChico", alignment: "right" },
    ]);
  }
  if (grupoActual) cargosAbonosTable.push(filaSubtotal(grupoActual));

  const diferenciaTotal = round2Decimals(cargosTotal - abonosTotal);

  let cancelado = {};
  if (poliza.estado === 0) {
    cancelado = { watermark: { text: "CANCELADO", color: "GRAY", opacity: 0.3, bold: true, italics: false, angle: -50, fontSize: 110 } };
  } else if (poliza.esPreview) {
    cancelado = { watermark: { text: "VISTA PREVIA", color: "#1677ff", opacity: 0.15, bold: true, italics: false, angle: -50, fontSize: 90 } };
  }

  // --- Metadata del encabezado (columna derecha del bloque emisor) ---
  const headerMeta = [
    ...(poliza?.tipodepoliza?.descripcion
      ? [{ label: `${poliza.tipodepoliza.descripcion}:`, value: "N° " + poliza.numeroTipoPoliza }]
      : []),
    { label: "Fecha:", value: moment(poliza.fechaRegistro).locale("es").format("LL") },
    { label: "Detallada:", value: detallada ? "Sí" : "No" },
    ...(poliza.numeroCheque ? [{ label: "N° Cheque:", value: String(poliza.numeroCheque) }] : []),
  ];

  // --- Proyecto / sub proyecto de la póliza ---
  // Con renglones de varios proyectos, el del encabezado es solo el principal:
  // el reparto real esta en el Desglose por Proyecto.
  const polizaLeft = [
    {
      label: mostrarColumnaProyecto ? "Proyecto principal:" : "Proyecto:",
      value: [poliza.proyecto?.nombre, poliza.proyecto?.descripcion].filter(Boolean).join(" ") || "N/A",
      strong: true,
    },
  ];

  const polizaRight = [
    {
      label: "Sub proyecto:",
      value: poliza?.subproyecto ? poliza.subproyecto.codigo + " - " + poliza.subproyecto.nombre : "N/A",
    },
  ];

  // Concepto a todo el ancho: es el texto libre de la póliza y suele ser largo.
  const conceptoNode = poliza.concepto
    ? {
      margin: [0, 3, 0, 0],
      fontSize: pdfFz(7),
      lineHeight: PDF_LINE_HEIGHT,
      text: [
        { text: "Concepto:  ", font: PDF_LABEL_FONT, bold: true, color: PDF_COLORS.label },
        { text: String(poliza.concepto), color: PDF_COLORS.ink },
      ],
    }
    : null;

  // --- Tabla de movimientos: encabezado + anchos ---
  const title = [
    { text: "Cuenta", style: "tituloTabla", alignment: "left" },
    { text: "Descripción", style: "tituloTabla", alignment: "left" },
    ...(mostrarColumnaProyecto ? [{ text: "Proyecto", style: "tituloTabla", alignment: "left" }] : []),
    { text: "Concepto", style: "tituloTabla", alignment: "left" },
    { text: "Cargo", style: "tituloTabla", alignment: "right" },
    { text: "Abono", style: "tituloTabla", alignment: "right" },
  ];

  const widths = mostrarColumnaProyecto
    ? ["11%", "27%", "17%", "22%", "11%", "12%"]
    : ["13%", "33%", "28%", "13%", "13%"];

  // --- Totales de la póliza (derecha) ---
  const totalsRows = [
    { label: "Total cargos:", value: dineroDisabledMask(cargosTotal) },
    { label: "Total abonos:", value: dineroDisabledMask(abonosTotal) },
  ];

  // Desglose por proyecto. La Diferencia debe dar cero en cada renglón: la nómina
  // emite renglones intercentro (Empresa.cuentaIntercentroId) justo para que cada
  // proyecto cuadre por separado. Si no da cero, esa cuenta no está configurada.
  const desgloseProyectos = mostrarColumnaProyecto
    ? [
      seccionHeadingV2("Desglose por proyecto"),
      PDFDataTableV2({
        widths: ["34%", "10%", "19%", "19%", "18%"],
        title: [
          { text: "Proyecto", style: "tituloTabla", alignment: "left" },
          { text: "Movs.", style: "tituloTabla", alignment: "right" },
          { text: "Cargos", style: "tituloTabla", alignment: "right" },
          { text: "Abonos", style: "tituloTabla", alignment: "right" },
          { text: "Diferencia", style: "tituloTabla", alignment: "right" },
        ],
        rows: [
          ...resumenProyectos.map((grupo) => {
            const diferencia = round2Decimals(grupo.cargos - grupo.abonos);
            return [
              { text: grupo.nombre, style: "textoTablaChico", alignment: "left" },
              { text: String(grupo.movimientos), style: "textoTablaChico", alignment: "right" },
              { text: dineroDisabledMask(grupo.cargos), style: "textoTablaChico", alignment: "right" },
              { text: dineroDisabledMask(grupo.abonos), style: "textoTablaChico", alignment: "right" },
              {
                text: dineroDisabledMask(diferencia),
                style: "textoTablaChico",
                alignment: "right",
                ...(diferencia !== 0 ? { bold: true, color: PDF_COLORS.danger } : {}),
              },
            ];
          }),
          [
            { text: "TOTAL", style: "textoTablaChico", bold: true, alignment: "left", fillColor: PDF_COLORS.tint },
            { text: String(cargosabonos.length), style: "textoTablaChico", bold: true, alignment: "right", fillColor: PDF_COLORS.tint },
            { text: dineroDisabledMask(cargosTotal), style: "textoTablaChico", bold: true, alignment: "right", fillColor: PDF_COLORS.tint },
            { text: dineroDisabledMask(abonosTotal), style: "textoTablaChico", bold: true, alignment: "right", fillColor: PDF_COLORS.tint },
            {
              text: dineroDisabledMask(diferenciaTotal),
              style: "textoTablaChico",
              bold: true,
              alignment: "right",
              fillColor: PDF_COLORS.tint,
              ...(diferenciaTotal !== 0 ? { color: PDF_COLORS.danger } : {}),
            },
          ],
        ],
      }),
    ]
    : [];

  const avisoCuentasPorCrear = cuentasPorCrear.length > 0
    ? [
      PDFAvisoV2({
        fillColor: PDF_COLORS.warning,
        lineas: [
          {
            text: `* ${cuentasPorCrear.length} ${cuentasPorCrear.length === 1 ? "cuenta marcada con asterisco todavía no existe" : "cuentas marcadas con asterisco todavía no existen"}: ${cuentasPorCrear.length === 1 ? "se dará de alta" : "se darán de alta"} automáticamente con ese código al generar la póliza.`,
            bold: true,
          },
          "El código puede recorrerse si antes se dan de alta otras cuentas bajo la misma cuenta mayor.",
        ],
      }),
    ]
    : [];

  // --- Documentos fiscales relacionados ---
  const tablaDocumentos = documentos.length > 0
    ? [
      seccionHeadingV2("Documentos fiscales"),
      PDFDataTableV2({
        widths: ["8%", "9%", "30%", "11%", "32%", "10%"],
        title: [
          { text: "Tipo", style: "tituloTabla", alignment: "left" },
          { text: "SAT", style: "tituloTabla", alignment: "left" },
          { text: "UUID", style: "tituloTabla", alignment: "left" },
          { text: "Referencia", style: "tituloTabla", alignment: "left" },
          { text: "Descripción", style: "tituloTabla", alignment: "left" },
          { text: "Monto", style: "tituloTabla", alignment: "right" },
        ],
        rows: documentos.map((doc) => {
          let referencia = "";
          let descripcion = "";
          let monto = "";
          let sat = "";
          if (doc.tipo === "venta") {
            referencia = doc.serieNumero || "";
            descripcion = doc.concepto || "";
            monto = doc.total != null ? dineroDisabledMask(doc.total) : "";
            sat = "Ingreso";
          } else if (doc.tipo === "factura") {
            referencia = [doc.codigoFactura, doc.numeroFactura].filter(Boolean).join(" ");
            descripcion = doc.descripcion || "";
            sat = "Ingreso";
          } else if (doc.tipo === "pago") {
            referencia = doc.serieNumero || "";
            descripcion = doc.fechaPago ? moment(doc.fechaPago).format("DD/MM/YYYY") : "";
            monto = doc.monto != null ? dineroDisabledMask(doc.monto) : "";
            sat = "Pago";
          }
          return [
            { text: doc.tipo.charAt(0).toUpperCase() + doc.tipo.slice(1), style: "textoTablaChico", alignment: "left" },
            { text: sat, style: "textoTablaChico", alignment: "left" },
            { text: doc.uuid || "", style: "textoTablaChico", alignment: "left" },
            { text: referencia, style: "textoTablaChico", alignment: "left" },
            { text: descripcion, style: "textoTablaChico", alignment: "left" },
            { text: monto, style: "textoTablaChico", alignment: "right" },
          ];
        }),
      }),
    ]
    : [];

  const folio = `${iedInicial ? iedInicial + " - " : ""}${poliza.numeroTipoTipoPoliza}`;

  const dd = {
    ...cancelado,
    info: {
      title: `Póliza ${folio}` + (poliza.concepto ? ` ( ${poliza.concepto} )` : ""),
      author: "BIWO ERP",
      subject: "Póliza contable",
      creator: "pdfmake",
    },
    content: [
      PDFHeaderV2({
        empresa,
        tituloPrincipal: ied ? "Póliza de " + ied : "Póliza",
        tituloFolio: "N° " + folio,
        regimenFiscal: mostrarRegimen,
        tipoComprobante: ied ? { clave: iedInicial, nombre: ied } : false,
        meta: headerMeta,
      }),

      PDFCompanyInfoV2({
        left: polizaLeft,
        right: polizaRight,
        labelWidthLeft: "auto",
        labelWidthRight: "auto",
        fontSize: pdfFz(6),
      }),
      ...(conceptoNode ? [conceptoNode] : []),
      divisorV2(PDF_COLORS.neutralDivider, 6, 1),
      PDFItemsTableV2({ title, conceptos: cargosAbonosTable, widths }),
      // Recuadro gris: cierra el bloque de totales para que no se lea como
      // parte de la tabla que puede venir abajo (desglose / documentos).
      PDFSummarySectionV2({ rows: totalsRows, emphasizeLast: false, recuadro: true }),
      ...avisoCuentasPorCrear,
      ...desgloseProyectos,
      ...tablaDocumentos,
    ],
    footer: PDFBrandFooterConNumeroV2(logoBiwo),
    images: {
      logo: empresa.logo.base64Completa,
    },
    styles: pdfStylesV2(empresa),
    defaultStyle: { font: MULISH_FONT },
    pageSize: "LETTER",
    pageMargins: PDF_PAGE_MARGINS,
  };

  return dd;
};

export default polizaPdfV2;
